import { Pipe, PipeTransform } from '@angular/core';
import { LangueService } from 'src/app/services/langue/langue.service';
import { TranslateService } from '@ngx-translate/core';
import { AdresseClient } from '../api/model/adresse-client.model';
import { LangueEnum } from '../constantes/Langue.enum';
import { AdressePhysiqueCredit } from '../api/model/adresse-physique-credit.model';
import { AdressePhysiqueBancaire } from '../api/model/adresse-physique-bancaire.model';

@Pipe({
  name: 'adresse',
  standalone: false,
})
export class AdressePipe implements PipeTransform {
  isFrancais: boolean;
  trail: string = '';
  avantAppartement: string = '';
  private readonly espaceInsecable = '\u00A0';
  private readonly regCodePostalByGroup: RegExp = /([^-\s]{3})(\s{0,1}?)([^-\s]{3})/;
  private readonly maskCodePostal: string = `$1${this.espaceInsecable}$3`;
  private readonly appartementFr: string = 'appartement';
  private readonly appartementEn: string = 'Apartment';

  constructor(
    private readonly langueService: LangueService,
    readonly translateService: TranslateService
  ) {}

  transform(coordonnees: AdresseClient | AdressePhysiqueCredit | AdressePhysiqueBancaire): string {
    if (!coordonnees) {
      return '';
    }
    this.isFrancais = this.langueService.getCurrentLanguage() === LangueEnum.FR;
    this.trail = this.isFrancais ? '' : ',';
    this.avantAppartement = this.isFrancais ? ', ' : '\n';
    let adresse: string = '';
    if ('numeroCiviqueNomRue' in coordonnees || 'numeroCivique' in coordonnees) {
      adresse = this.formatAdressePhysiqueBancaire(coordonnees);
    } else if ('adresseLigne1' in coordonnees || 'adresseLigne2' in coordonnees || 'adresseLigne3' in coordonnees) {
      adresse = this.formatAdresseClientHogan(coordonnees);
    } else {
      adresse = this.formatAdressePhysiqueCredit(coordonnees as AdressePhysiqueCredit);
    }
    return this.finalClean(adresse);
  }
  private formatAdresseClientHogan(coordonnees: AdresseClient): string {
    return `${this.cleanAdresse(coordonnees.adresseLigne1, coordonnees.adresseLigne2, coordonnees.adresseLigne3)}\n${this.clean(coordonnees.nomMunicipalite, this.trail)}${this.cleanProvinceCreditHogan(coordonnees.codeSubdivisionPays, this.isFrancais)}  ${this.cleanPostal(coordonnees.codePostal)}`.trim();
  }

  private formatAdressePhysiqueCredit(coordonnees: AdressePhysiqueCredit): string {
    return `${this.combinerTexteAdresse(coordonnees.texteAdresseLigne1, coordonnees.texteAdresseLigne2, coordonnees.texteAdresseLigne3)}${coordonnees.nomMunicipalite}${this.cleanProvince(coordonnees.codeSubdivisionPays)}  ${this.cleanPostal(coordonnees.codePostal)}`.trim();
  }

  private formatAdressePhysiqueBancaire(coordonnees: AdressePhysiqueBancaire): string {
    return `${this.cleanNumeroCiviqueNomRueBancaire(coordonnees)}${this.cleanMunicipaliteSubdivisionCodePostal(coordonnees)}${this.cleanPays(coordonnees)}`;
  }

  private combinerTexteAdresse(
    texteAdresseLigne1: string,
    texteAdresseLigne2: string,
    texteAdresseLigne3: string
  ): string {
    let exteAdresse = '';
    if (texteAdresseLigne1) exteAdresse += texteAdresseLigne1 + '\n';
    if (texteAdresseLigne2) exteAdresse += texteAdresseLigne2 + '\n';
    if (texteAdresseLigne3) exteAdresse += texteAdresseLigne3 + '\n';
    return exteAdresse;
  }

  private cleanAdresse(adresseLigne1: string, adresseLigne2: string, adresseLigne3: string): string {
    let adresse;
    if (adresseLigne1 && this.isFrancais) {
      adresseLigne1 = adresseLigne1.trim();
      const numero = adresseLigne1.substring(0, adresseLigne1.indexOf(' '));
      const rue = adresseLigne1.substring(adresseLigne1.indexOf(' ') + 1);
      adresse = numero ? numero + ', ' + rue : rue;
    } else {
      adresse = adresseLigne1 || '';
    }
    adresse += adresseLigne2 ? ' ' + adresseLigne2 : '';
    adresse += adresseLigne3 ? ' ' + adresseLigne3 : '';
    return adresse;
  }

  private cleanNumeroCiviqueNomRueBancaire(coordonnees: AdressePhysiqueBancaire): string {
    let adresse;
    if (coordonnees.numeroCivique || coordonnees.nomRue) {
      adresse = coordonnees.numeroCivique
        ? coordonnees.numeroCivique + (this.isFrancais ? ', ' : ' ') + coordonnees.nomRue
        : coordonnees.nomRue;
    } else if (coordonnees.numeroCiviqueNomRue) {
      adresse = coordonnees.numeroCiviqueNomRue;
    }
    adresse += coordonnees.numeroUnite ? this.avantAppartement + this.cleanUniteBancaire(coordonnees.numeroUnite) : '';
    return adresse;
  }

  private cleanMunicipaliteSubdivisionCodePostal(coordonnees: AdressePhysiqueBancaire): string {
    if (coordonnees.codePays === 'CA' || coordonnees.codePays === undefined) {
      const provinceCanada = this.cleanProvince(coordonnees.codeSubdivisionPays);
      const codePostal = this.cleanPostal(coordonnees.codePostal);
      const adresse = `${coordonnees.nomMunicipalite ? coordonnees.nomMunicipalite : ''}${provinceCanada}${codePostal ? '  ' + codePostal : ''}`;
      return adresse ? `\n${adresse}` : '';
    }
    if (coordonnees.nomMunicipalite !== undefined) {
      const province =
        this.isFrancais && coordonnees.codeSubdivisionPays
          ? `(${coordonnees.codeSubdivisionPays?.slice(-2)})`
          : coordonnees.codeSubdivisionPays?.slice(-2);
      return `\n${coordonnees.nomMunicipalite}${province ? ' ' + province : ''}${coordonnees.codePostal ? '  ' + coordonnees.codePostal : ''}`;
    }
    return `\n${coordonnees.texteComplementHorsCanada}`;
  }

  private cleanPays(coordonnees: AdressePhysiqueBancaire): string {
    return coordonnees.codePays ? `\n${coordonnees.codePays}` : '';
  }

  private getDescriptionCodeSubdivisionPays(value: string): string {
    let province = this.translateService.instant(`formulairePosteCanada.donneeReference.provinces.CA-${value}`);
    if (!province || province === `formulairePosteCanada.donneeReference.provinces.CA-${value}`) {
      province = this.translateService.instant(`formulairePosteCanada.donneeReference.provinces.${value}`); // Bancaire retour CA-QC au lieu de QC
      if (!province || province === `formulairePosteCanada.donneeReference.provinces.${value}`) {
        value = value?.includes('-') ? value.substring(value.indexOf('-') + 1).trim() : value;
        province = value || '';
      }
    }
    return province;
  }

  private cleanProvince(value: string): string {
    if (!value) {
      return '';
    }
    if (this.isFrancais) {
      const province = this.getDescriptionCodeSubdivisionPays(value);
      return !province ? '' : ` (${province})`;
    }
    return ' ' + value.slice(-2);
  }

  private cleanProvinceCreditHogan(value: string, isFrancais: boolean = false): string {
    const province = this.getDescriptionCodeSubdivisionPays(value);
    return !province ? '' : isFrancais ? ` (${province})` : ` ${province}`;
  }

  private cleanUniteBancaire(numeroUnite: string): string {
    return this.isFrancais
      ? this.appartementFr + this.espaceInsecable + numeroUnite
      : this.appartementEn + this.espaceInsecable + numeroUnite;
  }

  private cleanPostal(value: string): string {
    if (!value) {
      return '';
    }
    value = value.replace(this.regCodePostalByGroup, this.maskCodePostal);
    return value;
  }

  private clean(value: string, trail: string = ''): string {
    return value ? value + trail : '';
  }

  private finalClean(value: string): string {
    if (value.endsWith(',')) {
      value = value.slice(0, -1).trim();
    }
    return value;
  }
}
